import { Camera } from "@/shared/ui/icons";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { ChatAvatar, Infobulle } from "@/shared/ui";
import { useDialog } from "@/shell/dialog";

import { fileToResizedDataUrl } from "./imageUtils";

/**
 * AvatarPicker — la photo de la créatrice, ronde, qui s'ouvre sur le sélecteur
 * de fichiers du système.
 *
 * Click → file picker → image redimensionnee en 256x256 max, encode JPEG
 * base64, passee a `onChange`. Affiche la photo si `avatarUrl` est set, sinon
 * l'avatar par défaut de la pastille (`ChatAvatar`, stable pour un même nom) :
 * la couleur choisie se lit dans la palette du profil et sur la carte, plus sur
 * la photo.
 *
 * Reutilise dans la card (48 px), dans le profil (64 px) et dans l'onboarding.
 */
interface AvatarPickerProps {
  name: string;
  /** Plus portée par la photo (cf plus haut) ; gardée pour les appelants de l'onboarding. */
  color?: string;
  avatarUrl?: string;
  onChange: (dataUrl: string) => void;
  /** Le diamètre, en px. À défaut, tiré de `sizeClass`. */
  size?: number;
  /** Ancien nom du diamètre : des classes Tailwind (`h-12 w-12`, `h-20 w-20`) — lues pour en tirer `size`. */
  sizeClass?: string;
  /** Plus utilisé : l'initiale n'est plus dessinée. */
  textClass?: string;
  /** Taille de l'icone Camera au hover. */
  iconClass?: string;
  /** Stop propagation au click — utile pour eviter de declencher un parent. */
  stopPropagation?: boolean;
}

/** `h-16 w-16` → 64 (l'échelle Tailwind : 1 = 4 px). */
function diametreDe(sizeClass: string | undefined): number | undefined {
  const m = /(?:^|\s)h-(\d+)(?:\s|$)/.exec(sizeClass ?? "");
  return m ? Number(m[1]) * 4 : undefined;
}

export function AvatarPicker({
  name,
  avatarUrl,
  onChange,
  size,
  sizeClass,
  iconClass = "h-4 w-4",
  stopPropagation,
}: AvatarPickerProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const dialog = useDialog();
  const diametre = size ?? diametreDe(sizeClass) ?? 48;

  function trigger(e: React.MouseEvent) {
    if (stopPropagation) e.stopPropagation();
    inputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de re-uploader le meme fichier
    if (!file) return;
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      onChange(dataUrl);
    } catch (err) {
      console.error("[AvatarPicker] failed to load image", err);
      void dialog.alert({
        title: t("panels.creators.avatarPicker.tooltip"),
        message: t("panels.creators.avatarPicker.loadError"),
        variant: "error",
      });
    }
  }

  return (
    <>
      <Infobulle texte={t("panels.creators.avatarPicker.tooltip")}>
        <button
          type="button"
          onClick={trigger}
          aria-label={t("panels.creators.avatarPicker.tooltip")}
          style={{ width: diametre, height: diametre }}
          className="group/avatar regie-round relative block shrink-0 overflow-hidden transition-opacity hover:opacity-90"
        >
          <ChatAvatar src={avatarUrl} seed={name} size={diametre} />
          <span className="regie-round absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 transition-opacity group-hover/avatar:opacity-100">
            <Camera className={iconClass + " text-white"} />
          </span>
        </button>
      </Infobulle>
      {/* Natif et caché : il n'a pas de dessin à lui, il ouvre le sélecteur du système. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </>
  );
}
